import { shouldInvalidateScopeAfter401 } from "./scopeInvalidation";
import axios, { AxiosInstance, AxiosResponse } from "axios";
import { dispatchScopeInvalidated } from "@/utils/scopeStorage";

// Base URL para las APIs
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

// Configuración de axios con headers comunes
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 10000, // 10 segundos de timeout
});

// Errores de auth0-spa-js que indican que la sesión no es recuperable sin re-login
const AUTH_ERROR_CODES = new Set([
  "invalid_grant",
  "login_required",
  "missing_refresh_token",
  "consent_required",
  "unauthorized",
]);

const isAuthError = (error: unknown): boolean =>
  typeof error === "object" &&
  error !== null &&
  "error" in error &&
  AUTH_ERROR_CODES.has(String((error as { error: unknown }).error));

// Interceptor para agregar token automáticamente
export const setupApiClient = (
  getToken: () => Promise<string | null>,
  onAuthFailure: () => void | Promise<void>
) => {
  // Limpiar interceptors existentes para evitar duplicados
  apiClient.interceptors.request.clear();
  apiClient.interceptors.response.clear();

  // Evitar múltiples logouts en paralelo (varias requests fallando a la vez)
  let authFailureHandled = false;
  const handleAuthFailure = () => {
    if (authFailureHandled) return;
    authFailureHandled = true;
    Promise.resolve(onAuthFailure()).catch((error) => {
      if (import.meta.env.DEV) {
        console.error("Error handling auth failure:", error);
      }
    });
  };

  apiClient.interceptors.request.use(
    async (config) => {
      try {
        const token = await getToken();
        if (token && token.trim() !== "") {
          config.headers.Authorization = `Bearer ${token}`;
          return config;
        }
        throw new Error("No se pudo obtener un token de acceso.");
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error("Error getting token for request:", error);
        }
        // Nunca mandar la request sin Authorization: el backend respondería
        // 401 igual y perderíamos el motivo real del fallo.
        if (isAuthError(error)) {
          handleAuthFailure();
        }
        return Promise.reject(error);
      }
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Interceptor para manejar errores de token expirado
  apiClient.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error) => {
      if (error.response?.status === 401 && !error.config._retry) {
        // Marcar como ya reintentado para prevenir loops
        error.config._retry = true;

        try {
          await getToken();
          if (shouldInvalidateScopeAfter401(error.config.url)) dispatchScopeInvalidated();
          const originalRequest = error.config;
          return apiClient(originalRequest);
        } catch (refreshError) {
          if (import.meta.env.DEV) {
            console.error("Error refreshing token:", refreshError);
          }
          // Refresh imposible: cerrar sesión y volver al login en lugar de
          // dejar al usuario clavado en /error.
          if (isAuthError(refreshError)) {
            handleAuthFailure();
          }
        }
      }
      return Promise.reject(error);
    }
  );
};

export default apiClient;
